import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { resetModal, Spinner } from '@guryou/html-components';
import { cognitoChangeUserPassword, stateIsLoaded, stateIsLoading } from '@guryou/redux';

const ChangePasswordForm = ({ onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const changeUserPassword = useSelector(state => state.cognito.changePassword);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (stateIsLoaded(changeUserPassword)) {
      dispatch(resetModal());
      if (onClose) onClose();
    }
  }, [dispatch, changeUserPassword.version, changeUserPassword, onClose]);

  const onSubmit = data => {
    dispatch(cognitoChangeUserPassword(data.oldPassword, data.newPassword));
  };

  const handleNewPasswordChange = e => {
    setNewPassword(e.target.value);
  };

  const handleConfirmPasswordChange = e => {
    setConfirmPassword(e.target.value);
  };

  const checkIfPasswordMatch = newPassword === confirmPassword;
  const isLoading = stateIsLoading(changeUserPassword);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : (
        <>
          <div>
            <label htmlFor="oldPassword" className="block mb-2 text-sm font-medium text-gray-900">
              {t('ACCOUNT.oldPassword')}
            </label>
            <input
              type="password"
              id="oldPassword"
              {...register('oldPassword', { required: t('GENERAL.passwordRequired') })}
              className="w-full h-10 px-4 border border-gray-300 rounded-lg focus:outline-none transition-colors"
              onFocus={e => {
                e.target.style.borderColor = 'var(--feel-primary)';
                e.target.style.boxShadow = `0 0 0 2px color-mix(in srgb, var(--feel-primary) 25%, transparent)`;
              }}
              onBlur={e => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = '';
              }}
            />
            {errors.oldPassword && <p className="mt-1 text-sm text-red-600">{errors.oldPassword.message}</p>}
          </div>

          <div>
            <label htmlFor="newPassword" className="block mb-2 text-sm font-medium text-gray-900">
              {t('GENERAL.password')}
            </label>
            <input
              type="password"
              id="newPassword"
              {...register('newPassword', {
                required: t('GENERAL.passwordRequired'),
                minLength: {
                  value: 8,
                  message: t('ACCOUNT.passwordMinimumCharacters'),
                },
              })}
              onChange={handleNewPasswordChange}
              className="w-full h-10 px-4 border border-gray-300 rounded-lg focus:outline-none transition-colors"
              onFocus={e => {
                e.target.style.borderColor = 'var(--feel-primary)';
                e.target.style.boxShadow = `0 0 0 2px color-mix(in srgb, var(--feel-primary) 25%, transparent)`;
              }}
              onBlur={e => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = '';
              }}
            />
            {errors.newPassword && <p className="mt-1 text-sm text-red-600">{errors.newPassword.message}</p>}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block mb-2 text-sm font-medium text-gray-900">
              {t('PROFILE.confirmPass')}
            </label>
            <input
              type="password"
              id="confirmPassword"
              {...register('confirmPassword', {
                required: t('GENERAL.passwordRequired'),
              })}
              onChange={handleConfirmPasswordChange}
              className="w-full h-10 px-4 border border-gray-300 rounded-lg focus:outline-none transition-colors"
              onFocus={e => {
                e.target.style.borderColor = 'var(--feel-primary)';
                e.target.style.boxShadow = `0 0 0 2px color-mix(in srgb, var(--feel-primary) 25%, transparent)`;
              }}
              onBlur={e => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = '';
              }}
            />
            {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>}
          </div>

          {/* Error messages */}
          {changeUserPassword.error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{changeUserPassword.error.message}</p>
            </div>
          )}

          {newPassword.length >= 8 && confirmPassword && !checkIfPasswordMatch && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{t('ACCOUNT.newAndConfirmPassDontMatch')}</p>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
              {t('GENERAL.cancel')}
            </button>
            <button
              type="submit"
              disabled={!checkIfPasswordMatch || newPassword.length < 8}
              className={`px-6 py-2 text-white rounded-lg bg-[var(--feel-primary)] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
            >
              {t('PROVIDERS.changePassword')}
            </button>
          </div>
        </>
      )}
    </form>
  );
};

export default ChangePasswordForm;
